import type { ColorItem } from '@octopus-do/octopus-project-core'
import { useProjectLegacyActions } from '@store/migrationActions/useProjectLegacyActions'
import cx from 'classnames'
import type React from 'react'
import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react'

import ClickAwayListener from '../../App/ClickAwayListener/ClickAwayListener'
import css from './Legend.module.scss'

interface Props {
  showInput: string | null
  advanced: boolean
  isReadOnly: boolean
  onClick: () => void
  onSetShowInput: (id: string | null) => void
  color: ColorItem
}

export const LegendItem: React.FC<Props> = ({
  color,
  showInput,
  advanced,
  isReadOnly,
  onClick,
  onSetShowInput,
}) => {
  const { changeColorTitle } = useProjectLegacyActions()

  const { id, title, hexCode } = color
  const [value, setValue] = useState(title)
  const textareaRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (showInput) {
      textareaRef.current?.focus()
    }
  }, [showInput])

  useEffect(() => {
    setValue(title)
  }, [title])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    setValue(value)
  }

  const handleKeyDown = (evt: KeyboardEvent<HTMLInputElement>) => {
    if (evt.keyCode === 13) {
      const target = evt.target as HTMLInputElement

      target.blur()
      if (!value) {
        changeColorTitle({ id, title: value })
      }
    }
  }

  const handleOnBlur = (evt: FocusEvent<HTMLInputElement>) => {
    const value =
      evt.target.value.trim().length === 0 ? title : evt.target.value

    changeColorTitle({ id, title: value })

    setTimeout(() => onSetShowInput(null), 0)
  }

  return (
    <div
      key={id}
      className={cx(css.item, { [css.readOnly]: !advanced || isReadOnly })}
      onClick={onClick}
    >
      <div className={css.spotWrap}>
        <div className={css.spot} style={{ backgroundColor: hexCode }} />
      </div>
      {showInput === id ? (
        <ClickAwayListener onClickAway={() => onSetShowInput(null)}>
          <input
            ref={textareaRef}
            className={css.input}
            value={value}
            onChange={handleChange}
            onClick={(e) => e.stopPropagation()}
            onBlur={handleOnBlur}
            onKeyDown={handleKeyDown}
          />
        </ClickAwayListener>
      ) : (
        <div
          className={cx(css.text, { [css.disabled]: isReadOnly })}
          onClick={(e) => {
            e.stopPropagation()

            if (isReadOnly) {
              return
            }
            onSetShowInput(id)
          }}
        >
          {title}
        </div>
      )}
    </div>
  )
}
